(() => {
    const runWhenReady = (callback) => {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', callback, { once: true });
        } else {
            callback();
        }
    };

    if (window.__filterPopupControllerLoaded) {
        runWhenReady(() => {
            if (window.filterPopupController && typeof window.filterPopupController.refresh === 'function') {
                window.filterPopupController.refresh();
            }
        });
        return;
    }
    window.__filterPopupControllerLoaded = true;

    class FilterPopup {
        constructor() {
            this.toggleButtonHandler = (event) => {
                event.preventDefault();
                this.togglePopup(event.currentTarget);
            };
            this.handleDocumentKeydown = (event) => {
                if (event.key === 'Escape' && this.filterPopup && this.filterPopup.classList.contains('active')) {
                    this.closePopup();
                }
            };
            this.filterButtons = [];
            this.lastTriggerButton = null;
            this.bodyOverflowCache = undefined;
            this.prevFilterPopup = null;
            this.sortInputClickHandler = null;

            this.captureButtons();
            this.captureOverlayElements();
            this.bindToggleButtons();
            document.addEventListener('keydown', this.handleDocumentKeydown);

            this.refresh();
            document.addEventListener('filter-popup:refresh', () => this.refresh());
        }

        captureButtons() {
            const buttons = Array.from(document.querySelectorAll('.filter-toggle-btn'));
            if (!buttons.length) {
                this.filterButtons = [];
                return;
            }

            this.filterButtons = buttons;
            const firstLabel = buttons[0].textContent.replace(/\s*[+\-]\s*$/, '').trim();
            this.baseFilterText = firstLabel || this.baseFilterText || 'Filter';

            this.filterButtons.forEach((button) => {
                const baseText = button.textContent.replace(/\s*[+\-]\s*$/, '').trim() || this.baseFilterText;
                button.dataset.filterBase = baseText;
            });
        }

        bindToggleButtons() {
            const overlayAvailable = Boolean(this.filterPopup && document.body.contains(this.filterPopup));

            this.filterButtons.forEach((button) => {
                button.removeEventListener('click', this.toggleButtonHandler);
                button.classList.toggle('filter-toggle-btn--disabled', !overlayAvailable);

                if (overlayAvailable) {
                    button.addEventListener('click', this.toggleButtonHandler);
                    button.removeAttribute('disabled');
                    button.removeAttribute('aria-disabled');
                } else {
                    button.setAttribute('disabled', 'disabled');
                    button.setAttribute('aria-disabled', 'true');
                }
            });
        }

        refresh() {
            this.captureButtons();
            this.captureOverlayElements();
            this.bindOverlayListeners();
            this.bindToggleButtons();
            this.updateButtonText();
            this.syncFormStateFromURL();
            this.enforceFilterOptionOrder('filter.p.m.custom.sex', ['homme', 'femme', 'unisex']);
        }

        captureOverlayElements() {
            this.filterPopup = document.querySelector('#filter-popup');
            this.form = this.filterPopup ? this.filterPopup.querySelector('.filter-popup-form') : null;
            this.filterPopupContent = this.form;
            this.closeButton = this.filterPopup ? this.filterPopup.querySelector('.filter-close-btn') : null;
            this.clearButton = this.filterPopup ? this.filterPopup.querySelector('.filter-action-btn--clear') : null;
            this.applyButton = this.filterPopup ? this.filterPopup.querySelector('.filter-action-btn--apply') : null;
            this.defaultSort = this.form ? this.form.dataset.defaultSort || '' : '';
        }

        bindOverlayListeners() {
            if (this.prevFilterPopup && this.popupClickHandler) {
                this.prevFilterPopup.removeEventListener('click', this.popupClickHandler);
            }

            if (this.prevCloseButton && this.closeButtonHandler) {
                this.prevCloseButton.removeEventListener('click', this.closeButtonHandler);
            }
            if (this.prevClearButton && this.clearButtonHandler) {
                this.prevClearButton.removeEventListener('click', this.clearButtonHandler);
            }
            if (this.prevApplyButton && this.applyButtonHandler) {
                this.prevApplyButton.removeEventListener('click', this.applyButtonHandler);
            }
            if (this.prevForm && this.formChangeHandler) {
                this.prevForm.removeEventListener('change', this.formChangeHandler);
            }
            if (this.prevForm && this.formSubmitHandler) {
                this.prevForm.removeEventListener('submit', this.formSubmitHandler);
            }
            if (this.prevForm && this.sortInputClickHandler) {
                this.prevForm.removeEventListener('click', this.sortInputClickHandler);
            }

            this.prevFilterPopup = null;
            this.prevCloseButton = null;
            this.prevClearButton = null;
            this.prevApplyButton = null;
            this.prevForm = null;

            if (!this.filterPopup) {
                return;
            }

            this.popupClickHandler = (event) => this.handlePopupClick(event);
            this.filterPopup.addEventListener('click', this.popupClickHandler);
            this.prevFilterPopup = this.filterPopup;

            this.closeButtonHandler = (event) => {
                event.preventDefault();
                this.closePopup();
            };
            if (this.closeButton) {
                this.closeButton.addEventListener('click', this.closeButtonHandler);
                this.prevCloseButton = this.closeButton;
            }

            this.clearButtonHandler = (event) => {
                event.preventDefault();
                this.clearAllFilters();
            };
            if (this.clearButton) {
                this.clearButton.addEventListener('click', this.clearButtonHandler);
                this.prevClearButton = this.clearButton;
            }

            this.applyButtonHandler = (event) => {
                event.preventDefault();
                this.applyFilters();
            };
            if (this.applyButton) {
                this.applyButton.addEventListener('click', this.applyButtonHandler);
                this.prevApplyButton = this.applyButton;
            }

            if (this.form) {
                this.formChangeHandler = (event) => this.handleFormChange(event);
                this.formSubmitHandler = (event) => {
                    event.preventDefault();
                    this.applyFilters();
                };
                this.sortInputClickHandler = (event) => this.handleSortInputClick(event);
                this.form.addEventListener('change', this.formChangeHandler);
                this.form.addEventListener('submit', this.formSubmitHandler);
                this.form.addEventListener('click', this.sortInputClickHandler);
                this.prevForm = this.form;
            }
        }

        updateButtonText(forceOpenState) {
            const isOpen = typeof forceOpenState === 'boolean'
                ? forceOpenState
                : Boolean(this.filterPopup && this.filterPopup.classList.contains('active'));
            this.filterButtons.forEach((button) => {
                const baseText = button.dataset.filterBase || this.baseFilterText || 'Filter';
                button.textContent = `${baseText} ${isOpen ? '-' : '+'}`;
                button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
            });
        }

        togglePopup(triggerButton) {
            if (!this.filterPopup || !document.body.contains(this.filterPopup)) {
                this.captureOverlayElements();
                this.bindOverlayListeners();
                if (!this.filterPopup || !document.body.contains(this.filterPopup)) {
                    if (triggerButton) {
                        triggerButton.setAttribute('disabled', 'disabled');
                        triggerButton.setAttribute('aria-disabled', 'true');
                        triggerButton.classList.add('filter-toggle-btn--disabled');
                    }
                    return;
                }
            }

            if (triggerButton) {
                this.lastTriggerButton = triggerButton;
            }

            if (this.filterPopup.classList.contains('active')) {
                this.closePopup();
            } else {
                this.openPopup();
            }
        }

        openPopup() {
            if (!this.filterPopup) {
                return;
            }

            this.syncFormStateFromURL();
            this.filterPopup.classList.add('active');
            this.filterPopup.setAttribute('aria-hidden', 'false');
            this.updateButtonText(true);
            if (typeof this.bodyOverflowCache === 'undefined') {
                this.bodyOverflowCache = document.body.style.overflow;
            }
            document.body.style.overflow = 'hidden';

            if (this.filterPopupContent) {
                window.requestAnimationFrame(() => this.filterPopupContent.focus());
            }
        }

        closePopup() {
            if (!this.filterPopup) {
                return;
            }

            this.filterPopup.classList.remove('active');
            this.filterPopup.setAttribute('aria-hidden', 'true');
            this.updateButtonText(false);
            if (typeof this.bodyOverflowCache !== 'undefined') {
                document.body.style.overflow = this.bodyOverflowCache;
            } else {
                document.body.style.overflow = '';
            }

            this.bodyOverflowCache = undefined;

            if (this.lastTriggerButton) {
                this.lastTriggerButton.focus();
            }
        }

        handlePopupClick(event) {
            if (event.target === this.filterPopup) {
                this.closePopup();
            }
        }

        handleFormChange(event) {
            const input = event.target;
            if (!input || !input.classList.contains('filter-option-input')) {
                return;
            }

            if (input.type === 'checkbox') {
                const label = input.closest('.filter-option');
                if (label && !label.classList.contains('disabled')) {
                    label.classList.toggle('active', input.checked);
                }
            }

            if (input.type === 'radio' && input.name === 'sort_by') {
                this.form
                    .querySelectorAll('.filter-option--sort')
                    .forEach((label) => label.classList.remove('active'));
                const label = input.closest('.filter-option');
                if (label) {
                    label.classList.add('active');
                }
            }
        }

        syncFormStateFromURL() {
            if (!this.form) {
                return;
            }

            const params = new URLSearchParams(window.location.search);

            const checkboxInputs = this.form.querySelectorAll('input[type="checkbox"]');
            checkboxInputs.forEach((input) => {
                const values = params.getAll(input.name);
                const isChecked = this.inputValueMatches(values, input.value);
                input.checked = isChecked;
                const label = input.closest('.filter-option');
                if (label) {
                    label.classList.toggle('active', isChecked);
                }
            });

            const sortInputs = this.form.querySelectorAll('input[name="sort_by"]');
            if (sortInputs.length) {
                const currentSort = params.get('sort_by') || '';
                if (!currentSort) {
                    this.clearSortSelection();
                } else {
                    sortInputs.forEach((input) => {
                        const isActive = input.value === currentSort;
                        input.checked = isActive;
                        const label = input.closest('.filter-option');
                        if (label) {
                            label.classList.toggle('active', isActive);
                        }
                    });
                }
            }

            const priceInputs = this.form.querySelectorAll('.filter-price-input');
            priceInputs.forEach((input) => {
                const value = params.get(input.name) || '';
                input.value = value;
            });
        }

        inputValueMatches(values, target) {
            if (!values || !values.length || !target) {
                return false;
            }

            if (values.includes(target)) {
                return true;
            }

            const normalizedTarget = target.trim().toLowerCase();
            return values.some((value) => (value || '').trim().toLowerCase() === normalizedTarget);
        }

        clearAllFilters() {
            if (!this.form) {
                return;
            }

            this.form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
                if (input.disabled) {
                    return;
                }
                input.checked = false;
                const label = input.closest('.filter-option');
                if (label) {
                    label.classList.remove('active');
                }
            });

            this.clearSortSelection();

            this.form.querySelectorAll('.filter-price-input').forEach((input) => {
                input.value = '';
            });
        }

        clearSortSelection() {
            if (!this.form) {
                return;
            }

            const sortInputs = this.form.querySelectorAll('input[name="sort_by"]');
            sortInputs.forEach((input) => {
                input.checked = false;
                const label = input.closest('.filter-option');
                if (label) {
                    label.classList.remove('active');
                }
            });
        }

        handleSortInputClick(event) {
            const target = event.target;
            if (!target || typeof target.closest !== 'function') {
                return;
            }
            const input = target.closest('.filter-option-input--sort');
            if (!input || input.type !== 'radio') {
                return;
            }

            const label = input.closest('.filter-option');
            const isActive = Boolean(label && label.classList.contains('active'));
            if (!isActive) {
                return;
            }

            event.preventDefault();
            this.clearSortSelection();
        }

        buildSearchParams() {
            if (!this.form) {
                return window.location.search.slice(1);
            }

            const params = new URLSearchParams(window.location.search);
            const controlledKeys = new Set();

            this.form.querySelectorAll('input[name]').forEach((input) => {
                controlledKeys.add(input.name);
            });

            controlledKeys.forEach((key) => params.delete(key));

            const formData = new FormData(this.form);
            for (const [key, rawValue] of formData.entries()) {
                if (rawValue === null) {
                    continue;
                }
                const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
                if (value === '') {
                    continue;
                }
                params.append(key, value);
            }

            params.delete('page');
            return params.toString();
        }

        applyFilters() {
            const searchParams = this.buildSearchParams();

            let handledViaAjax = false;
            if (typeof FacetFiltersForm !== 'undefined' && typeof FacetFiltersForm.renderPage === 'function') {
                try {
                    FacetFiltersForm.searchParamsPrev = searchParams;
                    FacetFiltersForm.renderPage(searchParams, null, true);
                    handledViaAjax = true;
                } catch (error) {
                    // AJAX apply failed; falls back to a full page reload below.
                }
            }

            if (!handledViaAjax) {
                const path = window.location.pathname;
                const target = searchParams ? `${path}?${searchParams}` : path;
                window.location.assign(target);
            }

            this.closePopup();
        }

        enforceFilterOptionOrder(paramName, desiredOrder) {
            if (!this.form || !paramName || !Array.isArray(desiredOrder) || !desiredOrder.length) {
                return;
            }

            const grid = this.form.querySelector(`.filter-popup-section[data-filter-param="${paramName}"] .filter-options-grid`);
            if (!grid) {
                return;
            }

            desiredOrder.forEach((value) => {
                const normalizedValue = typeof value === 'string' ? value.trim() : '';
                if (!normalizedValue) {
                    return;
                }
                const input = grid.querySelector(`.filter-option-input[value="${normalizedValue}"]`);
                if (!input) {
                    return;
                }
                const optionLabel = input.closest('.filter-option');
                if (optionLabel) {
                    grid.appendChild(optionLabel);
                }
            });
        }
    }

    runWhenReady(() => {
        if (window.filterPopupController && typeof window.filterPopupController.refresh === 'function') {
            window.filterPopupController.refresh();
        } else {
            window.filterPopupController = new FilterPopup();
        }
    });
})();
